import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";

import api from "../../services/api";
import { describeEvent } from "../../utils/auditText";
import { exactTime, relativeTime } from "../../utils/time";

const MONTHS = ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avg", "sen", "okt", "noy", "dek"];
const MONTHS_LONG = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr",
];

// "2026-10-22" is a calendar day already in Tashkent time (the server buckets
// it); parsed by hand so the browser's own zone cannot shift it by one.
const dayParts = (iso) => {
  const [, m, d] = iso.split("-").map(Number);
  return { d, m: m - 1 };
};

const SERIES = {
  messages: { label: "Xabarlar", unit: "ta xabar" },
  threads: { label: "Yangi muhokamalar", unit: "ta yangi muhokama" },
};

const nf = new Intl.NumberFormat("uz-UZ");
const n = (v) => nf.format(v ?? 0);

/** "+12% oldingi haftaga" -- or just the two numbers when there is no base. */
const Delta = ({ now, before }) => {
  if (!before) {
    return now ? <span>bu hafta +{n(now)}</span> : <span>bu hafta yoʻq</span>;
  }
  const pct = Math.round(((now - before) / before) * 100);
  return (
    <>
      <span className="ms-delta">
        <Icon icon={pct >= 0 ? "tabler:arrow-up-right" : "tabler:arrow-down-right"} />
        {Math.abs(pct)}%
      </span>
      <span>
        bu hafta {n(now)}, oldin {n(before)}
      </span>
    </>
  );
};

/**
 * The staff home screen (rebuilt 2026-10-22).
 *
 * Read top to bottom, it answers: how big is the system and is it growing
 * (the four numbers, each against the week before); is it being used (30 days
 * of messages or new threads); what needs a person today (setup gaps on live
 * clients, ticket logins refused in the last 24 hours, a relay that stopped
 * draining); who uses it most; and what staff changed lately (the audit log).
 *
 * One call for the numbers (/dashboard/), one for the recent actions
 * (/audit/). Days are Tashkent days, counted by the server.
 */
const OverviewPage = () => {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [series, setSeries] = useState("messages");

  useEffect(() => {
    let live = true;
    api
      .get("/dashboard/")
      .then((res) => live && setData(res.data))
      .catch(() => live && setError("Maʼlumotlarni yuklab boʻlmadi."));
    api
      .get("/audit/", { params: { limit: 8 } })
      .then((res) => live && setEvents(res.data.results || []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const chart = useMemo(() => {
    if (!data) return null;
    const days = data.daily;
    const unit = SERIES[series].unit;
    return {
      series: [{ name: SERIES[series].label, data: days.map((d) => d[series]) }],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "Golos Text, system-ui, sans-serif",
          animations: { enabled: false },
        },
        colors: ["#073aa5"],
        plotOptions: {
          bar: { columnWidth: "62%", borderRadius: 4, borderRadiusApplication: "end" },
        },
        dataLabels: { enabled: false },
        // Fewer date labels on a phone, or they run into each other.
        responsive: [{ breakpoint: 640, options: { xaxis: { tickAmount: 4 } } }],
        states: { hover: { filter: { type: "darken", value: 0.85 } } },
        grid: {
          borderColor: "#eef1f6",
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          padding: { left: 6, right: 6 },
        },
        xaxis: {
          categories: days.map((d) => d.date),
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { colors: "#667085", fontSize: "11px" },
            formatter: (iso) => {
              if (!iso) return "";
              const { d, m } = dayParts(iso);
              return `${d}-${MONTHS[m]}`;
            },
          },
          tickAmount: 10,
          axisBorder: { color: "#e6e9ef" },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: "#667085", fontSize: "11px" },
            formatter: (v) => (Number.isInteger(v) ? n(v) : ""),
          },
        },
        tooltip: {
          theme: "light",
          x: {
            formatter: (_, { dataPointIndex }) => {
              const iso = days[dataPointIndex]?.date;
              if (!iso) return "";
              const { d, m } = dayParts(iso);
              return `${d}-${MONTHS_LONG[m]}`;
            },
          },
          y: { formatter: (v) => `${n(v)} ${unit}`, title: { formatter: () => "" } },
          marker: { show: false },
        },
      },
    };
  }, [data, series]);

  if (error) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  const { totals, week, outbox } = data;
  const total30 = data.daily.reduce((s, d) => s + d[series], 0);
  const topMax = Math.max(1, ...data.top_clients.map((c) => c.messages));
  // Pending rows are normal: the gateway marks them sent in its hourly sweep,
  // long after the message reached everyone. Only `stuck` (older than two
  // sweeps, decided by the server) means the sweeper has stopped.
  const backlog = outbox?.pending ?? 0;
  const stuck = outbox?.stuck ?? 0;
  const attention = [
    ...(stuck > 0
      ? [{ kind: "outbox", key: "outbox", count: stuck, since: outbox.oldest_pending_at }]
      : []),
    ...data.attention.map((a, i) => ({ ...a, key: `${a.kind}-${a.client_id}-${i}` })),
  ];

  return (
    <>
      {/* The relay's state, first (the tech lead's call): one glance at the top
          says whether messages are flowing. When it stops, it also heads
          "Diqqat talab qiladi". */}
      <div className="ms-sysline ms-sysline--top">
        <b style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon icon="tabler:send" style={{ color: "var(--ms-muted)" }} />
          Xabar tarqatish
        </b>
        <span className={`ms-badge ms-badge--${stuck === 0 ? "success" : "danger"}`}>
          {stuck === 0 ? "sogʻlom" : "navbat tiqilib qoldi"}
        </span>
        <span>
          Tarqatish navbati: <b>{n(backlog)}</b>
          {backlog > 0 && stuck === 0 && (
            <small style={{ color: "var(--ms-muted)" }}> (soatlik belgilash kutilmoqda)</small>
          )}
        </span>
        <span>
          Yuborilgan: <b>{n(outbox?.published_total)}</b>
        </span>
        <span>
          Eng eski navbat:{" "}
          <b>{outbox?.oldest_pending_at ? relativeTime(outbox.oldest_pending_at) : "—"}</b>
        </span>
      </div>

      <div className="ms-dash-kpis">
        <Link className="ms-dkpi" to="/muhokamalar">
          <div className="ms-dkpi-top">
            <span>Xabarlar</span>
            <Icon icon="tabler:message-2" />
          </div>
          <div className="ms-dkpi-value">{n(totals.messages)}</div>
          <div className="ms-dkpi-foot">
            <Delta {...week.messages} />
          </div>
        </Link>
        <Link className="ms-dkpi" to="/muhokamalar">
          <div className="ms-dkpi-top">
            <span>Muhokamalar</span>
            <Icon icon="tabler:messages" />
          </div>
          <div className="ms-dkpi-value">{n(totals.threads)}</div>
          <div className="ms-dkpi-foot">
            <Delta {...week.threads} />
          </div>
        </Link>
        <Link className="ms-dkpi" to="/shaxslar">
          <div className="ms-dkpi-top">
            <span>Shaxslar</span>
            <Icon icon="tabler:users" />
          </div>
          <div className="ms-dkpi-value">{n(totals.people)}</div>
          <div className="ms-dkpi-foot">
            <Delta {...week.people} />
          </div>
        </Link>
        <Link className="ms-dkpi" to="/mijoz-tizimlari">
          <div className="ms-dkpi-top">
            <span>Mijoz tizimlari</span>
            <Icon icon="tabler:server-2" />
          </div>
          <div className="ms-dkpi-value">
            {n(totals.active_clients)}
            <small>faol / {n(totals.clients)}</small>
          </div>
          <div className="ms-dkpi-foot">
            <span>{n(totals.members)} ta faol aʼzolik</span>
          </div>
        </Link>
      </div>

      <div className="ms-dash-row">
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:chart-bar" />
            <h2>Faollik — oxirgi 30 kun</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              jami {n(total30)} {SERIES[series].unit}
            </span>
            <div className="ms-toolbar-spacer" />
            <div className="ms-segmented" role="group" aria-label="Koʻrsatkich">
              {Object.entries(SERIES).map(([key, s]) => (
                <button
                  key={key}
                  type="button"
                  className={series === key ? "is-active" : ""}
                  aria-pressed={series === key}
                  onClick={() => setSeries(key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ms-chart-wrap">
            <Chart type="bar" height={268} series={chart.series} options={chart.options} />
          </div>
        </div>

        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:alert-circle" />
            <h2>Diqqat talab qiladi</h2>
            {attention.length > 0 && (
              <span className="ms-tab-count is-danger">{attention.length}</span>
            )}
          </div>
          {attention.length === 0 ? (
            <div className="ms-attn-ok">
              <Icon icon="tabler:circle-check" />
              <span>Hammasi joyida — hozircha eʼtibor talab qiladigan narsa yoʻq.</span>
            </div>
          ) : (
            <ul className="ms-attn">
              {attention.slice(0, 7).map((a) => (
                <li key={a.key}>
                  {a.kind === "outbox" ? (
                    <div>
                      <Icon icon="tabler:send-off" />
                      <span>
                        Tarqatish navbatida {n(a.count)} ta yozuv 2 soatdan beri turibdi
                        <small>
                          eng eskisi {a.since ? relativeTime(a.since) : "—"} — gateway navbatni
                          belgilamayapti, uni tekshiring
                        </small>
                      </span>
                    </div>
                  ) : a.kind === "refused_logins" ? (
                    <Link to={`/mijoz-tizimlari/${a.client_id}/azolar`}>
                      <Icon icon="tabler:login-2" />
                      <span>
                        {a.client_name}: {n(a.count)} ta kirish rad etildi
                        <small>
                          oxirgi 24 soatda, {n(a.people)} kishi — aʼzo emas yoki olib tashlangan
                        </small>
                      </span>
                    </Link>
                  ) : (
                    <Link to={`/mijoz-tizimlari/${a.client_id}/${a.missing.includes("aʼzo") && a.missing.length === 1 ? "azolar" : "ulanish"}`}>
                      <Icon icon="tabler:plug-x" />
                      <span>
                        {a.client_name}: sozlanmagan
                        <small>{a.missing.join(", ")} yoʻq</small>
                      </span>
                    </Link>
                  )}
                </li>
              ))}
              {attention.length > 7 && (
                <li>
                  <Link to="/mijoz-tizimlari">
                    <Icon icon="tabler:dots" style={{ color: "var(--ms-muted)" }} />
                    <span>yana {attention.length - 7} ta — Mijoz tizimlari</span>
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="ms-dash-row ms-dash-row--half">
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:trophy" />
            <h2>Eng faol mijoz tizimlari</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              xabarlar, oxirgi 30 kun
            </span>
          </div>
          {data.top_clients.length === 0 ? (
            <div className="ms-state">
              <Icon icon="tabler:message-off" />
              <span>Oxirgi 30 kunda xabar yoʻq.</span>
            </div>
          ) : (
            <div className="ms-table-wrap">
              <table className="ms-table ms-table--compact">
                <thead>
                  <tr>
                    <th>Mijoz tizimi</th>
                    <th style={{ width: "40%" }}>Xabarlar</th>
                    <th style={{ width: 100 }}>Muhokamalar</th>
                    <th style={{ width: 120 }}>Oxirgi xabar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_clients.map((c) => (
                    <tr key={c.id}>
                      <td className="ms-td-strong ms-td-clamp">
                        <Link className="ms-sublink" to={`/mijoz-tizimlari/${c.id}/muhokamalar`}>
                          {c.name}
                        </Link>
                      </td>
                      <td>
                        <div className="ms-dmeter" title={`${n(c.messages)} ta xabar`}>
                          <span>
                            <i style={{ width: `${(c.messages / topMax) * 100}%` }} />
                          </span>
                          <b>{n(c.messages)}</b>
                        </div>
                      </td>
                      <td className="ms-td-num">{n(c.threads)}</td>
                      <td className="ms-td-muted" title={exactTime(c.last_message_at)}>
                        {c.last_message_at ? relativeTime(c.last_message_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:history" />
            <h2>Soʻnggi amallar</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              panel va kabinetda
            </span>
          </div>
          {events.length === 0 ? (
            <div className="ms-state">
              <Icon icon="tabler:history-off" />
              <span>Hali amal yozilmagan.</span>
            </div>
          ) : (
            <div className="ms-table-wrap">
              <table className="ms-table ms-table--compact">
                <tbody>
                  {events.map((e) => {
                    const { icon, text, to } = describeEvent(e);
                    return (
                      <tr key={e.id}>
                        <td className="ms-td-clamp">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <Icon icon={icon} style={{ color: "var(--ms-muted)", flex: "0 0 auto" }} />
                            {to ? (
                              <Link className="ms-sublink" to={to} title={text}>
                                {text}
                              </Link>
                            ) : (
                              <span title={text}>{text}</span>
                            )}
                          </span>
                        </td>
                        <td className="ms-td-muted ms-td-clamp" style={{ width: 150 }}>
                          {e.actor || "—"}
                        </td>
                        <td
                          className="ms-td-muted"
                          style={{ width: 110, whiteSpace: "nowrap" }}
                          title={exactTime(e.created_at)}
                        >
                          {relativeTime(e.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

export default OverviewPage;
